import { z } from "zod";

import type { SessionTypeCategory } from "../constants/session-types";

const SESSION_TYPE_VALUES: [SessionTypeCategory, ...SessionTypeCategory[]] = [
  "INDIVIDUAL_RELATIONSHIP_SUPPORT",
  "COUPLE_COUNSELLING",
  "BREAKUP_HEALING",
  "MARRIAGE_SUPPORT",
  "EMOTIONAL_SUPPORT",
  "COMMUNICATION_ISSUES",
  "FAMILY_PARTNER_CONFLICT",
  "OTHER",
];

export const concernSchema = z.object({
  discussTopic: z
    .string()
    .trim()
    .min(1, "Please share what you'd like to discuss.")
    .max(1000, "Please keep this under 1000 characters."),
  concernCategory: z.enum(SESSION_TYPE_VALUES, {
    message: "Please select a category.",
  }),
  relationshipStatus: z.string().trim().min(1, "Please select your relationship status."),
  isFirstSession: z.boolean(),
  preferredLanguage: z.string().trim().min(1, "Please select a preferred language."),
  bookingFor: z.enum(["self", "couple"], {
    message: "Please select who this session is for.",
  }),
  urgencyLevel: z.enum(["low", "medium", "high"], {
    message: "Please select an urgency level.",
  }),
  additionalNotes: z
    .string()
    .trim()
    .max(2000, "Please keep additional notes under 2000 characters.")
    .optional()
    .or(z.literal("")),
});

export type ConcernFormValues = z.infer<typeof concernSchema>;

export const CONCERN_FORM_DEFAULTS: ConcernFormValues = {
  discussTopic: "",
  concernCategory: "OTHER",
  relationshipStatus: "",
  isFirstSession: true,
  preferredLanguage: "",
  bookingFor: "self",
  urgencyLevel: "low",
  additionalNotes: "",
};

/** Validates raw form values and returns either the parsed data or field errors. */
export function validateConcernForm(
  values: ConcernFormValues,
):
  | { success: true; data: ConcernFormValues }
  | { success: false; errors: Partial<Record<keyof ConcernFormValues, string>> } {
  const result = concernSchema.safeParse(values);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors: Partial<Record<keyof ConcernFormValues, string>> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0] as keyof ConcernFormValues;
    if (key && !errors[key]) {
      errors[key] = issue.message;
    }
  }
  return { success: false, errors };
}
